import { StepType, TourProvider } from "@reactour/tour";
import { CompanyParams } from "const/routes";
import { TourStepContent } from "pages/Companies/CompanyId/Dashboard/Overview/components/TourStepContent";
import { useDashboardTourSteps } from "pages/Companies/CompanyId/Dashboard/Overview/hooks/use-tour-steps.hooks";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { TourType } from "types";

export type TourStep = {
  title: string;
  description: string;
  selector: string;
};

export const DashboardTourProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tourReady, setTourReady] = useState(false);

  const { companyId } = useParams<CompanyParams>() as CompanyParams;

  const {
    steps: dashboardTourSteps,
    companyLoading,
    showGetStartedChecklist,
  } = useDashboardTourSteps(companyId);

  useEffect(() => {
    const checkElementsAvailable = () => {
      if (companyLoading) return;
      const selectors = dashboardTourSteps.map((step) => step.selector);

      const allElementsExist =
        selectors.every((selector) => {
          return document.querySelector(selector) !== null;
        }) &&
        (showGetStartedChecklist
          ? document.querySelector(".checklist-tour-step") !== null
          : document.querySelector(".action-items-tour-step") !== null);

      setTourReady(allElementsExist);
      return allElementsExist;
    };

    checkElementsAvailable();

    const observer = new MutationObserver(checkElementsAvailable);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [tourReady, companyLoading, showGetStartedChecklist]);

  if (companyLoading) {
    return <>{children}</>;
  }

  const preventScroll = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  const steps: StepType[] = dashboardTourSteps.map((step, index) => ({
    selector: step.selector,
    content: (
      <TourStepContent
        currentStep={index}
        heading={step.title}
        description={step.description}
        steps={dashboardTourSteps.length}
        setCurrentStep={setCurrentStep}
        tourType={TourType.CompanyDashboard}
      />
    ),
  }));

  return (
    <TourProvider
      steps={steps}
      showBadge={false}
      showCloseButton={false}
      showDots={false}
      showNavigation={false}
      onClickMask={() => {}}
      padding={{
        mask: 0,
      }}
      afterOpen={() => {
        window.addEventListener("wheel", preventScroll, { passive: false });
        window.addEventListener("touchmove", preventScroll, { passive: false });
      }}
      beforeClose={() => {
        window.removeEventListener("wheel", preventScroll);
        window.removeEventListener("touchmove", preventScroll);
      }}
      currentStep={currentStep}
      styles={{
        popover: (base) => ({
          ...base,
          padding: 0,
          marginTop: currentStep >= 1 && currentStep <= 3 ? 20 : 0,
          marginLeft: currentStep === 0 ? 20 : 10,
          borderRadius: 13,
          color: "black",
        }),
        maskArea: (base) => ({ ...base, rx: currentStep === 0 ? 25 : 15 }),
      }}
    >
      {children}
    </TourProvider>
  );
};
